import { z } from "zod";
import { localStore, useStore } from "../../lib/store";

/**
 * The generations this device launched and has not yet reported the outcome of.
 *
 * It is what turns the completion toast into a TRANSITION rather than a state.
 * An artifact that is already `ready` when the app boots — a poster generated
 * last week, reopened from a chat — is not an outcome that just happened, and
 * announcing it would both invent a completion and report a latency of days.
 * The chat card used to hold that distinction in a `useRef`, which is precisely
 * why leaving the page lost the outcome: a ref survives neither the navigation
 * nor the webview reload this feature exists to allow. localStorage does.
 *
 * The entry is removed the moment the outcome is delivered, so the list is
 * normally empty and the watcher polls nothing at all.
 */
const watchedVisualAbstractsSchema = z.array(
  z.object({
    id: z.uuid(),
    // Carried rather than re-fetched: the outcome analytics name the article,
    // and by the time the poster is ready the page that knew it is long gone.
    articleId: z.string(),
    // Same reason, for the toast rather than the analytics: the completion card
    // names the paper, and the status endpoint the watcher polls answers about
    // one enum column — it never carries the brief the title lives in.
    articleTitle: z.string(),
    watchedAt: z.string().datetime(),
  })
);

export type WatchedVisualAbstract = z.infer<
  typeof watchedVisualAbstractsSchema
>[number];

/**
 * A run that has not settled within a day is never going to be announced: the
 * job is bounded by Inngest's own retries, so an entry this old means the
 * outcome was already seen elsewhere, or the artifact was deleted. Dropping it
 * silently is the point — expiring into a toast would tell the user their
 * poster is ready a day after they stopped caring.
 */
export const VISUAL_ABSTRACT_WATCH_TTL_MS = 24 * 60 * 60 * 1000;

const store = localStore<WatchedVisualAbstract[]>(
  "visual-abstract-watch",
  [],
  watchedVisualAbstractsSchema
);

export function useWatchedVisualAbstracts(): WatchedVisualAbstract[] {
  return useStore(store)[0];
}

/**
 * Reads through to the store rather than to a render snapshot. The watcher
 * settles entries inside an effect, and an effect that ran twice against a
 * captured list would announce the same outcome twice.
 */
export function readWatchedVisualAbstracts(): WatchedVisualAbstract[] {
  return store.get();
}

export function watchVisualAbstract(entry: {
  id: string;
  articleId: string;
  articleTitle: string;
}): void {
  store.set((previous) =>
    previous.some((watched) => watched.id === entry.id)
      ? previous
      : [...previous, { ...entry, watchedAt: new Date().toISOString() }]
  );
}

/**
 * Whether a poster this device asked for is still being generated.
 *
 * Read by the foreground-push handler, which drops its own toast while this is
 * true: the same completion arrives as an FCM push AND as this watcher's poll,
 * and the watcher's card is the better of the two — it holds the artifact, so
 * it wears the poster's mark and its View opens the preview directly instead of
 * bouncing through the notification router. The cost is an unrelated push
 * arriving during a generation, which is silenced with it; the window is the
 * minute a run takes, and the notification centre keeps the record either way.
 */
export function isAwaitingVisualAbstract(): boolean {
  return store.get().length > 0;
}

export function unwatchVisualAbstract(id: string): void {
  store.set((previous) => previous.filter((watched) => watched.id !== id));
}

/**
 * The runs this device watched to a FAILED end, kept so the desktop library can
 * hold a "Not generated" card with a retry in the slot the generating card
 * occupied. The library route lists ready posters only, so without this list
 * the failure would leave no trace once its toast is gone.
 *
 * `roomId` comes from the settled artifact rather than the watch entry: it is
 * what the retry sends back, and the status route already carries it.
 *
 * A failure is keyed by its article and room, not by its artifact: the server
 * dedupes generations on that pair, so a later run of the same pair replaces
 * the failure, and any success of the pair (a retry, the creation screen, the
 * article pill, the chat) clears it. Keyed by artifact, a card would outlive the
 * poster that superseded it, and its retry would be answered with that ready
 * poster, which the watcher would then announce as a fresh completion.
 *
 * Local to the device, like the watch list it comes from, and bounded by the
 * same TTL: a failure a day old is no longer something the reader is waiting
 * to act on.
 */
const failedVisualAbstractsSchema = z.array(
  z.object({
    id: z.uuid(),
    articleId: z.string(),
    articleTitle: z.string(),
    roomId: z.string().nullable(),
    failedAt: z.string().datetime(),
  })
);

export type FailedVisualAbstract = z.infer<
  typeof failedVisualAbstractsSchema
>[number];

const failedStore = localStore<FailedVisualAbstract[]>(
  "visual-abstract-failed",
  [],
  failedVisualAbstractsSchema
);

export function useFailedVisualAbstracts(): FailedVisualAbstract[] {
  return useStore(failedStore)[0];
}

export function readFailedVisualAbstracts(): FailedVisualAbstract[] {
  return failedStore.get();
}

export function recordFailedVisualAbstract(entry: {
  id: string;
  articleId: string;
  articleTitle: string;
  roomId: string | null;
}): void {
  failedStore.set((previous) => [
    ...previous.filter(
      (failed) =>
        failed.articleId !== entry.articleId || failed.roomId !== entry.roomId
    ),
    { ...entry, failedAt: new Date().toISOString() },
  ]);
}

export function dismissFailedVisualAbstract(pair: {
  articleId: string;
  roomId: string | null;
}): void {
  failedStore.set((previous) =>
    previous.filter(
      (failed) =>
        failed.articleId !== pair.articleId || failed.roomId !== pair.roomId
    )
  );
}

/**
 * Both lists are the signed-in account's: the runs it launched and the ones it
 * saw fail, with their paper titles. Sign-out empties them, like the query
 * cache, so the next account on this browser is not shown someone else's
 * generating or failed cards, nor offered a retry on their article.
 */
export function clearVisualAbstractRunsOnDevice(): void {
  store.set([]);
  failedStore.set([]);
}
